import React from 'react';
import PropTypes from 'prop-types';

import TextField from '@visa/vds-react/input-styled';

import { StructFlex } from '@cybs-ui/components/lib/struct-flex';
import CybsButton from '@cybs-ui/components/lib/button';
import CybsChip from '@cybs-ui/components/lib/chip';

import styles from './trigger.styles';

const DropdownTrigger = ({
  elementProps,
  elementLabel,
  elementType,
  elementValue,
  focusRef,
  getToggleButtonProps,
  getInputProps,
  isOpen,
  dataTest
}) => {
  const TriggerElement = (type, label, value, props) => {
    switch (type) {
      case 'chip':
        return (
          <CybsChip
            dataTest={`${dataTest}-trigger-cybsAdvancedDropdown`}
            active={isOpen}
            isEditable
            {...props}
          >
            {label}
          </CybsChip>
        );
      case 'select': {
        const { inputWidth, ...rest } = props;
        return (
          <TextField
            data-test={`${dataTest}-trigger-cybsAdvancedDropdown`}
            css={theme => styles(theme, inputWidth).triggerInput}
            label={label}
            type="text"
            readOnly
            value={value}
            width={inputWidth}
          >
            <CybsButton
              dataTest={`${dataTest}-trigger-arrow-cybsAdvancedDropdown`}
              ariaLabel="Dropdown"
              iconClass={isOpen ? 'arrow-collapse' : 'arrow-expand'}
              iconType="light-tiny"
              resolution="tiny"
              variant="icon"
              {...rest}
            />
          </TextField>
        );
      }
      case 'column-header': {
        const { active, ...rest } = props; // eslint-disable-line
        return (
          <StructFlex
            tabIndex={0}
            align-items="center"
            justify-content="space-between"
            css={theme =>
              isOpen || active
                ? styles(theme).filterDropdownActive
                : styles(theme).filterDropdown
            }
            data-test={`${dataTest}-trigger-cybsAdvancedDropdown`}
          >
            <span>{elementLabel}</span>
            <CybsButton
              dataTest={`${dataTest}-trigger-arrow-cybsAdvancedDropdown`}
              {...rest}
              ref={focusRef}
              ariaLabel="Dropdown"
              iconClass={isOpen ? 'arrow-collapse' : 'arrow-expand'}
              iconType={`${isOpen || active ? 'dark' : 'light'}-tiny`}
              resolution="tiny"
              variant="icon"
            />
          </StructFlex>
        );
      }
      case 'icon-menu': {
        return (
          <CybsButton
            dataTest={`${dataTest}-trigger-cybsAdvancedDropdown`}
            active={isOpen}
            label={label}
            variant="icon-menu"
            {...props}
          />
        );
      }
      case 'button':
      default: {
        const { iconClass, ...rest } = props;
        let icon = iconClass;
        if (!icon) {
          icon = isOpen ? 'arrow-collapse' : 'arrow-expand';
        }

        if (isOpen && iconClass) {
          rest.iconType = 'light-fill';
        }
        return (
          <CybsButton
            {...rest}
            iconClass={icon}
            dataTest={`${dataTest}-trigger-cybsAdvancedDropdown`}
          />
        );
      }
    }
  };

  TriggerElement.displayName = 'TriggerElement';

  TriggerElement.defaultProps = {
    inputWidth: '100px'
  };

  TriggerElement.propTypes = {
    inputWidth: PropTypes.string
  };

  return TriggerElement(elementType, elementLabel, elementValue, {
    ...elementProps,
    ...getToggleButtonProps({}),
    ...getInputProps({
      'aria-autocomplete': 'none'
    })
  });
};

DropdownTrigger.displayName = 'Trigger';

DropdownTrigger.defaultProps = {
  elementLabel: 'Dropdown',
  elementType: 'button',
  inputWidth: '100px'
};

DropdownTrigger.propTypes = {
  dataTest: PropTypes.string.isRequired,
  elementProps: PropTypes.objectOf(PropTypes.any),
  elementLabel: PropTypes.string.isRequired,
  elementType: PropTypes.oneOf(['chip', 'button', 'icon-button', 'select']),
  icon: PropTypes.string,
  getToggleButtonProps: PropTypes.func.isRequired,
  inputWidth: PropTypes.string
};

export default DropdownTrigger;
